import { useCallback, useRef } from "react"
import { useTranslation } from "react-i18next"
import { parseUtcIso } from "./parseUtcIso"
import type { TelemetryPoint } from "./fuelReportTypes"

export type SparkMarkerKind = "alert" | "refuel" | "seen"
export type SparkMarker = { at: string; kind: SparkMarkerKind }

const MARKER_STYLE = {
  alert: { color: "#2563eb", label: "reports.fuelSparkline.alertSent" },
  refuel: { color: "#14ae5c", label: "reports.fuelSparkline.refuel" },
  seen: { color: "#7c3aed", label: "reports.fuelSparkline.seen" },
} as const satisfies Record<SparkMarkerKind, { color: string; label: string }>

// SVG canvas: x is scaled to the container (preserveAspectRatio="none"), so
// only vertical lines / horizontal paths are safe primitives here.
const VIEW_W = 100
const VIEW_H = 36
const PAD_Y = 3

/** Fuel value normalized to a 0..1 fraction (telemetry may carry 0..100). */
export function normalizedFuelFraction(points: TelemetryPoint[]): number[] {
  const values = points.map((p) => p.fuelLevel ?? NaN)
  const max = Math.max(...values.filter((v) => !Number.isNaN(v)), 0)
  const scale = max > 1.5 ? 100 : 1
  return values.map((v) => (Number.isNaN(v) ? NaN : v / scale))
}

/** Index of the telemetry point closest in time to `tMs`. */
export function nearestIndexAtTime(
  points: TelemetryPoint[],
  tMs: number,
): number {
  let best = 0
  let bestDelta = Infinity
  for (let i = 0; i < points.length; i++) {
    const delta = Math.abs(parseUtcIso(points[i].timestamp).getTime() - tMs)
    if (delta < bestDelta) {
      bestDelta = delta
      best = i
    }
  }
  return best
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x))
}

/**
 * Fuel-level-over-time chart for the detail dialog: the fuel curve with
 * vertical markers for the alert, the refuel, and the seen moment, plus the
 * current playback position. Click or drag to scrub the playback.
 */
export function FuelSparkline({
  points,
  markers,
  index,
  onIndexChange,
}: {
  points: TelemetryPoint[]
  markers: SparkMarker[]
  index: number
  onIndexChange: (i: number) => void
}) {
  const { t } = useTranslation()
  const svgRef = useRef<SVGSVGElement>(null)

  const t0 = points.length
    ? parseUtcIso(points[0].timestamp).getTime()
    : Number.NaN
  const t1 = points.length
    ? parseUtcIso(points[points.length - 1].timestamp).getTime()
    : Number.NaN

  const scrubTo = useCallback(
    (clientX: number) => {
      const rect = svgRef.current?.getBoundingClientRect()
      if (!rect || rect.width === 0 || !points.length || t1 <= t0) return
      const frac = clamp01((clientX - rect.left) / rect.width)
      onIndexChange(nearestIndexAtTime(points, t0 + frac * (t1 - t0)))
    },
    [points, t0, t1, onIndexChange],
  )

  if (points.length < 2 || Number.isNaN(t0) || t1 <= t0) return null

  const xOf = (tMs: number) => clamp01((tMs - t0) / (t1 - t0)) * VIEW_W
  const fuel = normalizedFuelFraction(points)
  const yOf = (fraction: number) =>
    PAD_Y + (1 - clamp01(fraction)) * (VIEW_H - 2 * PAD_Y)

  const lineD = points
    .map((p, i) => ({ x: xOf(parseUtcIso(p.timestamp).getTime()), f: fuel[i] }))
    .filter(({ f }) => !Number.isNaN(f))
    .map(
      ({ x, f }, i) =>
        `${i === 0 ? "M" : "L"}${x.toFixed(2)},${yOf(f).toFixed(2)}`,
    )
    .join(" ")

  const currentX = xOf(
    parseUtcIso(
      points[Math.max(0, Math.min(index, points.length - 1))].timestamp,
    ).getTime(),
  )

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="none"
      className="h-16 w-full cursor-crosshair touch-none rounded border bg-muted/30"
      role="img"
      aria-label={t("reports.fuelSparkline.ariaLabel")}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId)
        scrubTo(e.clientX)
      }}
      onPointerMove={(e) => {
        if (e.buttons === 1) scrubTo(e.clientX)
      }}
    >
      {/* 50% gridline */}
      <line
        x1={0}
        x2={VIEW_W}
        y1={yOf(0.5)}
        y2={yOf(0.5)}
        stroke="currentColor"
        strokeOpacity={0.15}
        strokeDasharray="2 2"
        vectorEffect="non-scaling-stroke"
      />
      {/* fuel curve */}
      {lineD && (
        <path
          d={lineD}
          fill="none"
          stroke="#0284c7"
          strokeWidth={1.5}
          vectorEffect="non-scaling-stroke"
        />
      )}
      {/* event markers */}
      {markers.map((m, i) => {
        const at = parseUtcIso(m.at).getTime()
        if (Number.isNaN(at)) return null
        const { color, label } = MARKER_STYLE[m.kind]
        return (
          <line
            key={`${m.kind}-${i}`}
            x1={xOf(at)}
            x2={xOf(at)}
            y1={0}
            y2={VIEW_H}
            stroke={color}
            strokeWidth={1.5}
            vectorEffect="non-scaling-stroke"
          >
            <title>{t(label)}</title>
          </line>
        )
      })}
      {/* current playback position */}
      <line
        x1={currentX}
        x2={currentX}
        y1={0}
        y2={VIEW_H}
        stroke="currentColor"
        strokeOpacity={0.6}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}
